import React from 'react';
import { ExploreSearchResult } from 'features/explore/types/types';
import { getReportEntityFlag } from 'core/services/report-entities-service/report-entities-service';
import { Button } from 'ui-components/button/button';
import { useOpenExploreReportLink } from 'features/explore/hooks/use-explore-report-link';
import {
    useIsLockedPoi,
    useOnLockedButtonClick,
} from 'hooks/use-locked-poi-hooks/use-locked-poi-hooks';
import { GrayLockIcon } from 'components/assets/Icons/GrayLockIcon';
import { DropdownMenuButton } from 'ui-components/dropdown-menu-button/dropdown-menu-button';
import { ChevronIcon } from 'components/assets/Icons/Icons';
import { BillboardPOI as OldBillboard } from 'core/entities';
import { useExplorePermissions } from 'features/explore/hooks/use-explore-permissions';
import { useIsFreemiumUser } from 'hooks/use-freemium-user/use-freemium-user';
import { getDeviceType } from 'utils/get-user-agent-data/utils';
import { useExploreUIState } from 'features/explore/context/use-explore-ui-state';
import { useCheckCoordinateForNearbyReport } from 'features/explore/hooks/use-explore-nearby-activity';
import { useFullscreenContext } from 'utils/fullscreen-provider/fullscreen-provider';
import classNames from 'classnames';
import { isAnEnclosingComplexFlag } from 'utils/enclosing-complexes-filter/enclosing-complexes-filter';
import { isLoadingExploreEnclosingComplexesSignal } from 'features/explore/store/explore-signals/explore-encloasing-complexes-signal';
import {
    useDropdownOptions,
    useOnOpenReport,
} from 'features/explore/hooks/use-explore-poi-actions';
import styles from './explore-poi-actions-button.module.scss';
import { usePoiFactory } from 'features/explore/model/hooks/use-poi-factory';

type ExplorePoiActionsButtonProps = {
    poi: ExploreSearchResult;
    splunkComponent?: string;
};

const LockedButton = () => {
    const { hasFreemiumExpFF } = useExplorePermissions();
    const onLockedButtonClick = useOnLockedButtonClick();

    const text = hasFreemiumExpFF ? 'Report Options' : 'Upgrade Your Plan';

    return (
        <Button
            className={styles.lockedButton}
            onClick={onLockedButtonClick}
            data-testid="report-options-button-locked"
        >
            <div className={styles.lockedButtonInfo}>
                <GrayLockIcon className={styles.lockedButtonIcon} />
                <span className={styles.lockedButtonText}>{text}</span>
            </div>
        </Button>
    );
};

export const ExplorePoiActionsButton = ({ poi, splunkComponent }: ExplorePoiActionsButtonProps) => {
    const { isLoading } = useOpenExploreReportLink();
    const isLockedPoi = useIsLockedPoi();
    const { hasFreemiumExpFF } = useExplorePermissions();
    const isLocked = isLockedPoi(poi.info);
    const { activeId } = useExploreUIState();
    const { isFullscreen } = useFullscreenContext();
    const isFreemiumUser = useIsFreemiumUser();
    const flag = getReportEntityFlag(poi.info);
    const checkCoordinateForNearbyReport = useCheckCoordinateForNearbyReport();
    const isFreemiumAvailablePoi = isFreemiumUser && !flag;
    const isLoadingEnclosingComplexes = isLoadingExploreEnclosingComplexesSignal.value;

    const poiFactory = usePoiFactory();
    const poiInstance = poiFactory.createPOIByEntity(poi);

    const onOpenReport = useOnOpenReport(poiInstance, splunkComponent);
    const dropdownOptions = useDropdownOptions(poiInstance, splunkComponent);
    const isEnclosingPOI = isAnEnclosingComplexFlag(poi.info);
    const isLoadingEnclosingPOI = isEnclosingPOI && isLoadingEnclosingComplexes;

    if (isLocked) {
        return <LockedButton />;
    }

    const isTrafficPin = OldBillboard.isBillboard(poi.info);

    const { isLoading: isNearbyLoading } = checkCoordinateForNearbyReport(poi.info);

    const pinTitle = isTrafficPin ? 'Traffic Pin' : 'Property';

    if (isLoading && poi.info.id === activeId && poi.info.provider_data?.provider) {
        return (
            <Button type="brand-primary-full" block disabled>
                Loading...
            </Button>
        );
    }

    if (isFreemiumAvailablePoi && !hasFreemiumExpFF) {
        return (
            <Button
                data-testid={'open-report-button'}
                type="brand-primary-full"
                block
                onClick={() => onOpenReport(pinTitle, 'poi')}
            >
                Open Report
            </Button>
        );
    }

    return (
        <DropdownMenuButton
            trigger={getDeviceType() === 'desktop' ? ['hover'] : ['hover', 'click']}
            options={dropdownOptions}
            lockedClass={styles.lockedItem}
            tooltipPlacement={'right'}
            getPopupContainer={(triggerNode) => triggerNode.parentElement!}
            menuContainerClass={classNames(styles.dropdownMenuContainer, {
                [styles.fullScreen]: isFullscreen,
            })}
        >
            <Button
                className={styles.overrideLoading}
                loading={isNearbyLoading || isLoadingEnclosingPOI}
                data-testid={'open-report-button'}
                type="brand-primary-full"
                block
            >
                Open Report <ChevronIcon rotate={-90} />
            </Button>
        </DropdownMenuButton>
    );
};
